import React from 'react';
import queryString from 'query-string';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { RouteComponentProps } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { SignUp as SignUpDomain } from '@/domains';
import { signUpWithInvitation } from '@/services/api/users';
import { translations } from '@/locale';

import logo from '@/assets/logo.svg';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address').min(1, 'Email is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  invitationToken: z.string().min(1, 'Invitation token is required')
});

type FormValues = z.infer<typeof formSchema>;

export interface Props extends RouteComponentProps {}

const AcceptInvitationPage: React.FC<Props & WrappedComponentProps> = (props) => {
  const email = queryString.parse(location.search).email as string;
  const invitationToken = queryString.parse(location.search).invitationToken as string;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: email || '',
      password: '',
      invitationToken: invitationToken || ''
    }
  });

  async function onSubmit(values: FormValues) {
    try {
      await signUpWithInvitation(
        values.firstName,
        values.lastName,
        values.email,
        values.password,
        values.invitationToken
      );
    } catch (error) {
      console.error('Failed to sign up with invitation:', error);
    }
  }

  return (
    <div className="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img className="mx-auto h-12 w-auto" src={logo} alt="Workflow" />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Sign up</h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Create your account</CardTitle>
            <CardDescription>Fill in your details to complete the sign up process.</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{props.intl.formatMessage({ id: translations.inputs.firstName.label })}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={props.intl.formatMessage({ id: translations.inputs.firstName.label })}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{props.intl.formatMessage({ id: translations.inputs.lastName.label })}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={props.intl.formatMessage({ id: translations.inputs.lastName.label })}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{props.intl.formatMessage({ id: translations.inputs.email.label })}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={props.intl.formatMessage({ id: translations.inputs.email.placeholder })}
                          {...field}
                          disabled
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{props.intl.formatMessage({ id: translations.inputs.password.label })}</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder={props.intl.formatMessage({ id: translations.inputs.password.placeholder })}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  disabled={form.formState.isSubmitting || !form.formState.isDirty}
                >
                  {form.formState.isSubmitting ? 'Signing up...' : 'Sign up'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default injectIntl(AcceptInvitationPage);
